import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useVitalsTable } from "../../hooks/useVitalsTable";
import {
  Badge,
  Button,
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  DestructiveConfirmDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
  Input,
  Tabs,
  TabsList,
  TabsTrigger,
  useStateWithSetAction,
  VirtualizedDataTable,
  VirtualizedDataTableProps,
} from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { Plus, Table, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { TellescopeModelFromName, usePermission, useTellescopeCountQuery } from "@tellescope/tanstack-query";
import { useForm } from "@tellescope/forms";
import { SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { VitalsTableConfigDialog } from "../../components/vitals/VitalsTableConfigDialog";
import { DEFAULT_VITALS_VIEW, useShallowVitalsDefaultViewStore } from "../../store";
import { ConfigureTableViewButton } from "../../components/tables/ConfigureTableViewButton";
import { PageHeader } from "../../components";
import { PermissionWrapper } from "../../components/PermissionWrapper";

export const VitalsViewsPage = ({ maxTabCount = 3 }: { maxTabCount?: number } = {}) => {
  const { t } = useTranslation("vitals");
  const navigate = useNavigate();
  const params = useParams();
  const viewId = params.viewId;
  const { lastSelectedViewId, setLastSelectedViewId } = useShallowVitalsDefaultViewStore(
    "lastSelectedViewId",
    "setLastSelectedViewId"
  );

  // useEffect is justified here because this store is a localStorage store that we want to sync to
  useEffect(() => {
    if (viewId && viewId !== lastSelectedViewId) {
      setLastSelectedViewId(viewId);
    }
  }, [viewId, lastSelectedViewId, setLastSelectedViewId]);

  const setViewId = useCallback(
    (idOrUpdater: SetStateAction<string>) => {
      const newId = typeof idOrUpdater === "function" ? idOrUpdater(viewId ?? "all") : idOrUpdater;
      navigate(`/vitals/views/${newId}`);
    },
    [navigate, viewId]
  );

  const vitalsTable = useVitalsTable({ viewId, setViewId });

  const [isAddViewDialogOpen, setIsAddViewDialogOpen] = useStateWithSetAction(false, (isOpen) => {
    if (!isOpen) {
      addViewForm.resetForm();
      setIsSaveAsView(false);
    }
  });
  const [isSaveAsView, setIsSaveAsView] = useState(false);
  const [viewConfigDialogIsOpen, setViewConfigDialogIsOpen] = useState(false);
  const [isOtherViewsDialogOpen, setIsOtherViewsDialogOpen] = useState(false);

  const addViewForm = useForm({
    fields: {
      title: {
        type: "text",
        required: true,
        initialValue: "",
      },
    },
    validateOn: "blur",
    onSubmit: (values) => {
      if (!values.title) return;

      const saveAsFilter = isSaveAsView ? vitalsTable.selectedView.filter : undefined;

      vitalsTable.createTableViewMutation.mutateAsync(
        {
          page: `${vitalsTable.TELLESCOPE_2_TABLE_VIEW_PREFIX}Vitals`,
          title: values.title,
          columns: isSaveAsView ? vitalsTable.selectedView.columns : DEFAULT_VITALS_VIEW.columns,
          ...(saveAsFilter !== undefined ? { filter: saveAsFilter } : undefined),
        },
        {
          onSuccess: (res) => {
            if (isSaveAsView) {
              vitalsTable.resetDefaultView();
            }
            navigate(`/vitals/views/${res.id}`);
          },
        }
      );
      setIsAddViewDialogOpen(false);
    },
  });

  const { access: canDelete } = usePermission({
    model: "table_views",
    action: "delete",
  });
  const { access: canCreate } = usePermission({
    model: "table_views",
    action: "create",
  });

  const { viewsToDisplay, rolledUpViews } = vitalsTable.views.reduce<{
    viewsToDisplay: TellescopeModelFromName<"table_views">[];
    rolledUpViews: TellescopeModelFromName<"table_views">[];
  }>(
    (acc, curr) => {
      if (curr.id === viewId) {
        if (acc.viewsToDisplay.length === maxTabCount - 1) {
          const viewToMove = acc.viewsToDisplay.pop();
          if (viewToMove) acc.rolledUpViews.push(viewToMove);
          acc.viewsToDisplay.push(curr);
          return acc;
        }

        acc.viewsToDisplay.push(curr);
        return acc;
      }

      if (acc.viewsToDisplay.length < maxTabCount - 1) {
        acc.viewsToDisplay.push(curr);
      } else {
        acc.rolledUpViews.push(curr);
      }

      return acc;
    },
    { viewsToDisplay: [], rolledUpViews: [] }
  );

  const activeFiltersCount = vitalsTable.activeFiltersCount;
  const [deleteDialogIsOpen, setDeleteDialogIsOpen] = useState(false);
  const actionButtons = useMemo<VirtualizedDataTableProps<unknown>["actionButtons"]>(() => {
    const result: NonNullable<VirtualizedDataTableProps<unknown>["actionButtons"]> =
      vitalsTable.viewId === "all" || !canDelete
        ? []
        : [
            {
              icon: Trash2,
              label: "",
              variant: "destructive",
              hoverLabel: t("Delete View"),
              onClick: () => setDeleteDialogIsOpen(true),
            },
          ];

    const hasSorting =
      (!activeFiltersCount && vitalsTable.isDefaultViewModified) || !!vitalsTable.virtualizedTableProps.sorting.length;
    result.push(
      <ConfigureTableViewButton
        hasFilter={!!activeFiltersCount}
        hasSorting={hasSorting}
        onClick={() => setViewConfigDialogIsOpen(true)}
        hoverLabel={t("Configure View")}
        data-trackingid="vitals-table-configure-view"
        data-testid="vitals-configure-view-button"
      />
    );

    if (vitalsTable.isDefaultViewModified) {
      if (canCreate) {
        result.push({
          label: t("Save as View"),
          variant: "outline",
          onClick: () => {
            setIsSaveAsView(true);
            setIsAddViewDialogOpen(true);
          },
          trackingId: "vitals-table-save-default-as-view",
        });
      } else {
        result.push({
          label: t("Reset View"),
          variant: "outline",
          onClick: () => {
            vitalsTable.resetDefaultView();
          },
          trackingId: "vitals-table-reset-default-view",
        });
      }
    } else if (vitalsTable.hasLocalOverride) {
      result.push({
        label: t("Reset View"),
        variant: "outline",
        onClick: () => {
          vitalsTable.resetLocalOverride();
        },
        trackingId: "vitals-table-reset-local-override-view",
      });
    }

    return result;
  }, [activeFiltersCount, canCreate, canDelete, setIsAddViewDialogOpen, t, vitalsTable]);

  const unreviewedCountQuery = useTellescopeCountQuery({
    model: "enduser_observations",
    request: {
      mdbFilter: {
        reviewedAt: {
          $in: [null, ""],
        },
      },
    },
  });

  if (!viewId) {
    return <Navigate replace to="./all" />;
  }

  return (
    <div className="h-svh flex flex-col flex-1">
      <PageHeader />
      <div className="p-6">
        <div className="flex gap-2 items-center mb-4">
          <Tabs
            value={vitalsTable.viewId}
            data-trackingid="vitals-table-view-selector-tabs"
            data-testid="vitals-view-tabs"
          >
            <TabsList>
              <TabsTrigger value="all" asChild data-testid="vitals-all-tab">
                <Link to="/vitals/views/all">
                  {t("All")}
                  {!!unreviewedCountQuery.data?.count && (
                    <Badge variant="destructive" className="rounded-full text-xs">
                      {unreviewedCountQuery.data.count.toLocaleString()}
                    </Badge>
                  )}
                </Link>
              </TabsTrigger>
              {viewsToDisplay.map((view) => (
                <TabsTrigger key={view.id} value={view.id} asChild>
                  <Link to={`/vitals/views/${view.id}`}>{view.title}</Link>
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          {!!rolledUpViews.length && (
            <Dialog open={isOtherViewsDialogOpen} onOpenChange={setIsOtherViewsDialogOpen}>
              <DialogTrigger asChild>
                <button
                  className="p-0 border-0 bg-transparent cursor-pointer text-muted-foreground text-sm"
                  onClick={() => {
                    setIsOtherViewsDialogOpen(true);
                  }}
                >
                  {t("and {{amount}} more...", { amount: rolledUpViews.length })}
                </button>
              </DialogTrigger>
              <DialogContent className="p-0 border-0 bg-transparent shadow-none [&>[data-slot=dialog-close]]:top-3 [&>[data-slot=dialog-close]]:right-3 [&>[data-slot=dialog-close]]:focus:ring-0 [&>[data-slot=dialog-close]]:focus:ring-offset-0">
                <Command className="rounded-lg border max-w-lg bg-popover">
                  <CommandInput placeholder={t("Search views...")} className="h-12 p-3" />
                  <CommandList>
                    <CommandEmpty>{t("No results found.")}</CommandEmpty>
                    {rolledUpViews.map((view) => (
                      <CommandItem key={view.id} value={view.id} asChild className="px-4 py-3 cursor-pointer">
                        <Link to={`/vitals/views/${view.id}`}>
                          <Table />
                          {view.title}
                        </Link>
                      </CommandItem>
                    ))}
                  </CommandList>
                </Command>
              </DialogContent>
            </Dialog>
          )}
          <PermissionWrapper model="table_views" action="create">
            <Dialog open={isAddViewDialogOpen} onOpenChange={setIsAddViewDialogOpen}>
              <DialogTrigger asChild>
                <button
                  className="p-0 border-0 bg-transparent cursor-pointer outline-none focus:outline-none"
                  data-trackingid="vitals-table-create-table-view-dialog-open"
                  data-testid="vitals-add-view-button"
                >
                  <Plus className="w-6 h-6 text-muted-foreground hover:text-foreground transition-colors" />
                </button>
              </DialogTrigger>
              <DialogContent>
                <div className="space-y-1">
                  <DialogTitle className="font-medium text-sm">
                    {isSaveAsView ? t("Save as View") : t("Create New Table")}
                  </DialogTitle>
                  <DialogDescription className="text-xs">
                    {isSaveAsView
                      ? t("Enter a name for the new view. It will be saved with your current column configuration.")
                      : t("Enter a name for your new table view. Press Enter to add it to available views.")}
                  </DialogDescription>
                </div>
                <form {...addViewForm.getFormProps()} className="flex flex-col gap-2">
                  <Input
                    placeholder={t("View name...")}
                    {...addViewForm.getFieldProps("title")}
                    data-testid="vitals-add-view-name-input"
                    onKeyDownCapture={(e) => {
                      if (e.key === "Enter" && addViewForm.values.title) {
                        e.stopPropagation();
                        addViewForm.submitForm();
                      }
                    }}
                    autoFocus
                  />
                  <div className="flex justify-end gap-2">
                    <Button
                      onClick={() => {
                        setIsAddViewDialogOpen(false);
                      }}
                      variant="outline"
                    >
                      {t("Cancel")}
                    </Button>
                    <Button
                      type="submit"
                      disabled={!addViewForm.isValid}
                      data-trackingid="vitals-table-create-table-view-dialog-confirm"
                      data-testid="vitals-add-view-submit-button"
                    >
                      {isSaveAsView ? t("Save View") : t("Add View")}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </PermissionWrapper>
        </div>

        <VirtualizedDataTable
          {...vitalsTable.virtualizedTableProps}
          viewportHeight={{ offset: 270 }}
          enableSelection
          actionButtons={actionButtons}
        />

        {!vitalsTable.isLoadingViews && (
          <VitalsTableConfigDialog
            title={t("Configure {{tableViewName}}", {
              tableViewName: vitalsTable.selectedView.title || t("All"),
            })}
            open={viewConfigDialogIsOpen}
            onOpenChange={(open) => setViewConfigDialogIsOpen(open)}
            columnConfigFormProps={{
              combinedFieldToColumnConfigMap: vitalsTable.combinedFieldToColumnConfigMap,
              columnVisibility: vitalsTable.columnVisibility,
              orderedVisibleColumnIds: vitalsTable.orderedVisibleColumnIds,
              title: t("Column Sort Order"),
              sorting: vitalsTable.virtualizedTableProps.sorting,
              setSorting: vitalsTable.virtualizedTableProps.setSorting,
            }}
            onApplyConfig={vitalsTable.onApplyConfig}
            activeFilters={vitalsTable.activeFilters}
            onClearAll={
              vitalsTable.isDefaultViewModified
                ? () => {
                    vitalsTable.resetDefaultView();
                    setViewConfigDialogIsOpen(false);
                  }
                : undefined
            }
            isDirty={vitalsTable.isDefaultViewModified}
            sourceOptions={vitalsTable.sourceOptions}
          />
        )}
        <DestructiveConfirmDialog
          onConfirm={() => {
            vitalsTable.deleteTableViewMutation.mutateAsync(vitalsTable.viewId);
            navigate("/vitals/views/all");
          }}
          loading={vitalsTable.deleteTableViewMutation.isPending}
          open={deleteDialogIsOpen}
          onOpenChange={(open) => setDeleteDialogIsOpen(open)}
          description={t("This will permanently delete the view. This action cannot be undone.")}
        />
      </div>
    </div>
  );
};
